/**
 * /app/$tenantSlug/settings/security — Settings → Organization → Security
 * (passkeys + multi-method 2FA, spec §6.5). company_admin only (backend
 * RBAC is the boundary; the rail hides the leaf from everyone else).
 *
 * One switch: "Require 2FA for everyone in this company". No grace period —
 * once on, anyone without a method is sent to set one up on their next
 * request, so turning it on asks for confirmation and shows how many people
 * that affects. HR managers always need 2FA regardless. Each person manages
 * their own methods at Me → Profile → Security.
 */

import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ShieldCheck, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { toApiError } from "@/lib/api";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { membersWithout2FALabel, securityPolicyError } from "@/lib/two-factor-security";
import { useSecuritySettings, useUpdateSecuritySettings } from "@/hooks/use-security";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/settings/security")({
  component: SettingsSecurityRoute,
});

function SettingsSecurityRoute() {
  const { user, refresh } = useAuth();
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();
  const isCompanyAdmin = user?.role === "company_admin";
  const settingsQ = useSecuritySettings(isCompanyAdmin);
  const save = useUpdateSecuritySettings();
  const [confirmOn, setConfirmOn] = useState(false);

  const data = settingsQ.data;
  const affected = data?.members_without_2fa ?? 0;
  // The admin's own account: with no method, the server refuses to switch
  // the policy on (409 enrol_first) — they set up their own 2FA first.
  const selfAffected = user?.totp_enabled === false;

  function showRefusal(code: string, message: string) {
    const out = securityPolicyError(code, message);
    const action = out.offerOwnSetup
      ? {
          label: "Set Up 2FA",
          onClick: () => void navigate({ to: "/app/$tenantSlug/me/account/security", params: { tenantSlug } }),
        }
      : undefined;
    if (out.tone === "info") toast.info(out.message, { action });
    else toast.error(out.message, { action });
  }

  async function apply(next: boolean) {
    try {
      await save.mutateAsync(next);
      setConfirmOn(false);
      toast.success(next ? "2FA is now required for everyone." : "2FA is no longer required for everyone.");
      // /me.totp_required follows the policy.
      await refresh();
    } catch (err) {
      setConfirmOn(false);
      // A cancelled step-up (turning it off) is info, enrol_first (turning
      // it on with no method of your own) offers the setup.
      const apiErr = toApiError(err);
      showRefusal(apiErr.error, apiErr.message);
    }
  }

  function onToggle(next: boolean) {
    // Round 2 B-M5: the server refuses to switch it on while the acting
    // admin has no method (409 enrol_first) — say so before asking.
    if (next && selfAffected) {
      showRefusal("enrol_first", "");
      return;
    }
    if (next && affected > 0) {
      setConfirmOn(true);
      return;
    }
    void apply(next);
  }

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="max-w-3xl space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Security</h2>
            <p className="text-sm text-muted-foreground">
              Decide whether everyone in your company must use two-factor authentication — a
              passkey or an authenticator app — when they sign in.
            </p>
          </div>

          {!isCompanyAdmin ? (
            <Alert data-testid="security-policy-admin-only">
              <ShieldCheck className="h-4 w-4" />
              <AlertTitle>Only your company admin can change this</AlertTitle>
              <AlertDescription>
                You can manage your own passkeys and authenticator app at{" "}
                <Link
                  to="/app/$tenantSlug/me/account/security"
                  params={{ tenantSlug }}
                  className="font-medium underline underline-offset-4"
                >
                  Me → Profile → Security
                </Link>
                .
              </AlertDescription>
            </Alert>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>HR managers always need 2FA, whatever this is set to.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {settingsQ.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : settingsQ.isError || !data ? (
                  <Alert variant="destructive">
                    <AlertTitle>Couldn&apos;t load the security policy</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>{toApiError(settingsQ.error).message || "Please try again."}</p>
                      <Button size="sm" variant="outline" onClick={() => void settingsQ.refetch()}>
                        Try Again
                      </Button>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4 rounded-md border p-4">
                      <div className="space-y-1">
                        <Label htmlFor="require-2fa" className="text-sm font-medium">
                          Require 2FA for Everyone in This Company
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Everyone signs in with a password and then a passkey or authenticator
                          code. Each person sets up their own at Me → Profile → Security.
                        </p>
                      </div>
                      <Switch
                        id="require-2fa"
                        checked={data.require_2fa}
                        disabled={save.isPending}
                        onCheckedChange={onToggle}
                        data-testid="security-require-2fa"
                      />
                    </div>

                    <Alert data-testid="security-policy-count">
                      <TriangleAlert className="h-4 w-4" />
                      <AlertTitle>{membersWithout2FALabel(affected)}</AlertTitle>
                      <AlertDescription>
                        {data.require_2fa
                          ? "Anyone without 2FA is asked to set it up the next time they use WorkRight."
                          : "If you turn this on, people without 2FA will be asked to set it up straight away."}
                      </AlertDescription>
                    </Alert>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </SettingsShell>

      <AlertDialog open={confirmOn} onOpenChange={(v) => !save.isPending && setConfirmOn(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Require 2FA for everyone?</AlertDialogTitle>
            <AlertDialogDescription>
              {membersWithout2FALabel(affected)} People without 2FA will be asked to set it up
              straight away — it takes a few seconds with a passkey.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={save.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={save.isPending}
              onClick={(e) => {
                e.preventDefault();
                void apply(true);
              }}
              data-testid="security-require-2fa-confirm"
            >
              Require 2FA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
